/**
 * Browser Print via QZ Tray HTML Printing
 * Uses QZ Tray HTML printing (pixel format) as primary method
 * Falls back to window.print() if QZ Tray is not available
 *
 * Reference: https://qz.io/docs/pixel#html-printing
 */

import { isEmpty } from 'lodash-es'
import qz from 'qz-tray'
import { connectQZTray, isQZTrayAvailable } from './qzTrayPrinter'
import { currency } from '../formatting'
import { isOptionEnabled } from '../helper'
import { getTableDetailForReceipt } from './receiptTableHelper'
import { getReceiptContact } from './receiptContactHelper'
import { getReceiptWitholding } from './receiptWitholdingHelper'

// =====================
// PAPER SIZE RESOLVER (IN INCHES)
// =====================
const resolvePaperInches = (paper) => {
  // default printer biasa: A4
  const FALLBACK = { width: 8.27, height: 11.69 } // inches

  if (!paper) {
    return FALLBACK
  }

  // support custom object:
  // { width: 210, height: 297, unit: 'mm' } or { width: 8.27, height: 11.69, unit: 'in' }
  if (typeof paper === 'object' && paper.width && paper.height) {
    const unit = (paper.unit || 'in').toLowerCase()
    if (unit === 'mm') {
      return { width: paper.width / 25.4, height: paper.height / 25.4 }
    }
    if (unit === 'cm') {
      return { width: paper.width / 2.54, height: paper.height / 2.54 }
    }
    return { width: paper.width, height: paper.height } // inches
  }

  const key = String(paper).toUpperCase()

  // printer biasa
  if (key === 'A4') {
    return { width: 8.27, height: 11.69 }
  }
  if (key === 'A5') {
    return { width: 5.83, height: 8.27 }
  }
  if (key === 'LETTER') {
    return { width: 8.5, height: 11.0 }
  }
  if (key === 'LEGAL') {
    return { width: 8.5, height: 14.0 }
  }

  // thermal (masih support)
  if (key === '80MM' || key === '80') {
    return { width: 3.15, height: 11.69 }
  }
  if (key === '58MM' || key === '58') {
    return { width: 2.28, height: 11.69 }
  }

  return FALLBACK
}

/**
 * Generate HTML string for receipt
 * Format sesuai dengan thermal printer
 */
const generateReceiptHTML = (receiptData, printOptions = {}) => {
  const {
    printerFunction = 'struk',
    outlet = {},
    companyOptions = {},
    outletOptions = {},
    // paperWidth tidak digunakan di HTML karena sekarang responsive terhadap lebar kertas printer
  } = printOptions

  const formatDate = (date) => {
    const d = new Date(date)
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'Mei',
      'Jun',
      'Jul',
      'Agu',
      'Sep',
      'Okt',
      'Nov',
      'Des',
    ]
    const day = String(d.getDate()).padStart(2, '0')
    const month = months[d.getMonth()]
    const year = d.getFullYear()
    const hours = String(d.getHours()).padStart(2, '0')
    const minutes = String(d.getMinutes()).padStart(2, '0')
    return `${day} ${month} ${year} ${hours}:${minutes}`
  }

  const formatQty = (qty) => {
    return Math.abs(qty)
  }

  // Helper function to create row (left and right aligned)
  // Menggunakan div dengan display: table untuk kompatibilitas yang lebih baik
  const createRow = (left, right) => {
    const leftStr = String(left || '')
    const rightStr = String(right || '')
    return `<div style="display: table; width: 100%; font-family: 'Courier New', monospace;">
      <div style="display: table-row;">
        <div style="display: table-cell; text-align: left; padding-right: 10px; white-space: pre-wrap; word-break: break-word; vertical-align: top;">${leftStr}</div>
        <div style="display: table-cell; text-align: right; white-space: nowrap; vertical-align: top; width: 1%;">${rightStr}</div>
      </div>
    </div>`
  }

  // Helper function to create line
  // Menggunakan border untuk line yang responsive terhadap lebar kertas
  const createLine = () => {
    // Gunakan border-bottom untuk line yang selalu full width
    // Tidak perlu char parameter karena menggunakan border CSS
    return `<div style="border-bottom: 1px solid #000; margin: 2px 0; width: 100%;"></div>`
  }

  let html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <style>
        @page {
          size: auto;
          margin: 0;
          padding: 0;
        }
        @media print {
          @page {
            size: auto;
            margin: 0;
            padding: 0;
          }
          body {
            margin: 0 !important;
            padding: 0 !important;
          }
        }
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        html {
          margin: 0 !important;
          padding: 0 !important;
          width: 100%;
        }
        body {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          line-height: 1.4;
          margin: 0 !important;
          padding: 0 !important; /* Tidak ada padding di body, margin di-handle oleh QZ config */
          max-width: 100%;
          width: 100%;
          text-align: left !important;
          overflow-x: hidden;
          position: relative;
          left: 0 !important;
        }
        /* Container untuk memastikan konten tidak overflow */
        body > * {
          max-width: 100%;
          overflow-x: hidden;
        }
        .center {
          text-align: center;
          display: block;
          width: 100%;
          margin: 0 auto;
        }
        .left {
          text-align: left;
          display: block;
          width: 100%;
        }
        .bold {
          font-weight: bold;
        }
        .row {
          font-family: 'Courier New', monospace;
          white-space: pre;
          text-align: left;
        }
        /* Table layout untuk row alignment */
        .table-row {
          display: table;
          width: 100%;
          font-family: 'Courier New', monospace;
        }
        .table-row > div {
          display: table-cell;
          font-family: 'Courier New', monospace;
        }
        .table-row > div:first-child {
          text-align: left;
          padding-right: 10px;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .table-row > div:last-child {
          text-align: right;
          white-space: nowrap;
          width: 1%;
        }
      </style>
    </head>
    <body>
  `

  // Header Section
  if (isOptionEnabled(outletOptions?.print_style_show_outlet_name) && !isEmpty(outlet?.name)) {
    html += `<div class="center bold">${outlet.name}</div>`
  }

  if (
    isOptionEnabled(outletOptions?.print_style_show_company_name) &&
    !isEmpty(companyOptions?.property_name)
  ) {
    html += `<div class="center">${companyOptions.property_name}</div>`
  }

  const receiptContact = getReceiptContact(outlet, companyOptions)
  // Alamat (prioritas outlet, fallback perusahaan)
  if (
    isOptionEnabled(outletOptions?.print_style_show_address) &&
    !isEmpty(receiptContact.address)
  ) {
    html += `<div class="center">${receiptContact.address}</div>`
  }

  if (isOptionEnabled(outletOptions?.print_style_show_email) && companyOptions?.property_email) {
    html += `<div class="center">${companyOptions.property_email}</div>`
  }

  // Telepon (prioritas outlet, fallback perusahaan)
  if (isOptionEnabled(outletOptions?.print_style_show_phone) && receiptContact.phone) {
    html += `<div class="center">${receiptContact.phone}</div>`
  }

  // Printer function label
  if (printerFunction === 'checker') {
    html += `<div class="center bold">CHECKER</div>`
  }

  if (printerFunction === 'kitchen') {
    html += `<div class="center bold">DAPUR</div>`
  }

  html += '<br>'

  // Border line
  html += createLine('=')
  html += '<br>'

  // Order Information
  if (receiptData.queue_number_formatted) {
    html += createRow('No. Antrian', receiptData.queue_number_formatted)
  }
  if (isOptionEnabled(outletOptions?.print_style_show_order_number)) {
    html += createRow('No. Order', receiptData.ref_number || '')
  }
  html += createRow('Waktu', formatDate(receiptData.trans_date))
  html += createRow('Kasir', receiptData.user?.name || '')

  if (isOptionEnabled(outletOptions?.print_style_show_customer) && receiptData.contact?.name) {
    html += createRow('Pelanggan', receiptData.contact.name)
  }

  if (outletOptions?.print_style_show_order_type === '1' && receiptData.orderType?.name) {
    html += createRow('Jenis order', receiptData.orderType.name)
  }

  // Detail meja — ikut aturan show/hide jenis transaksi (print_style_show_order_type)
  const tableDetail = getTableDetailForReceipt(receiptData)
  if (outletOptions?.print_style_show_order_type === '1' && tableDetail) {
    html += createRow('Meja', tableDetail.table_name || `#${tableDetail.table_id}`)
    if (tableDetail.order_name) {
      html += createRow('Nama Order', tableDetail.order_name)
    }
    if (tableDetail.reserved_count) {
      html += createRow('Jumlah Tamu', String(tableDetail.reserved_count))
    }
  }

  // Border line
  html += createLine('=')
  html += '<br>'

  // Bawaan aktif: hanya '0' yang menyembunyikan deskripsi item
  const showItemDescription = outletOptions.print_style_show_product_description?.toString() !== '0'

  // Items Section
  if (printerFunction === 'struk' || printerFunction === 'bill') {
    receiptData.items?.forEach((item) => {
      html += `<div class="left">${item.product?.name}</div><br>`
      html += createRow(
        `${formatQty(item.qty)}x ${currency(item.price)}`,
        currency(item.amount || 0),
      )

      if (item.discount_amount > 0) {
        // Baris hadiah BOGO: tampilkan "Gratis - {nama promo}"
        const isReward = Number(item.price_rule_reward_id) > 0
        const rewardName = item.price_rule?.name || item.price_rule_reward?.rule_name || ''
        let discountLabel
        if (isReward) {
          discountLabel = rewardName ? `Gratis - ${rewardName}` : 'Gratis'
        } else {
          discountLabel = item.price_rule?.name || ''
        }
        html += createRow(discountLabel, `(${currency(item.discount_amount)})`)
      }

      const lineDesc = item.desc ?? item.description
      if (showItemDescription && lineDesc) {
        html += `<div class="left">> ${lineDesc}</div><br>`
      }
    })
  }

  if (printerFunction === 'checker' || printerFunction === 'kitchen') {
    receiptData.items?.forEach((item) => {
      html += `<div class="left">(${formatQty(item.qty)}) ${item.product?.name}</div><br>`
      const lineDesc = item.desc ?? item.description
      if (showItemDescription && lineDesc) {
        html += `<div class="left">> ${lineDesc}</div><br>`
      }
    })
  }

  const witholding = getReceiptWitholding(receiptData, outletOptions, 'Pemotongan')

  // Summary Section (for struk/bill only)
  if (printerFunction === 'struk' || printerFunction === 'bill') {
    html += createLine('=')
    html += '<br>'

    html += createRow(
      `Subtotal ${receiptData.items?.length || 0} produk`,
      currency(receiptData.amount || 0),
    )

    if (receiptData.additional_discount_amount > 0) {
      let discountLabel = 'Tambahan Diskon'
      if (receiptData.additional_discount_percent > 0) {
        discountLabel += ` (${receiptData.additional_discount_percent}%)`
      }
      html += createRow(discountLabel, `(${currency(receiptData.additional_discount_amount)})`)
    }

    if (receiptData.discount_per_item > 0) {
      html += createRow('Diskon per item', `(${currency(receiptData.discount_per_item)})`)
    }

    if (receiptData.taxes && Object.keys(receiptData.taxes).length > 0) {
      Object.keys(receiptData.taxes).forEach((key) => {
        const t = receiptData.taxes[key]
        const taxLabel = `${key} (${t.percent}%)`
        html += createRow(taxLabel, currency(Math.abs(t.value)))
      })
    } else if (receiptData.tax_amount) {
      const taxLabel = `${receiptData.tax?.name || 'Pajak'} (${receiptData.tax?.percent}%)`
      html += createRow(taxLabel, currency(Math.abs(receiptData.tax_amount)))
    }

    if (receiptData.totalMdrFee > 0) {
      html += createRow('Biaya tambahan', currency(receiptData.totalMdrFee))
    }

    // Pemotongan (bisa disembunyikan per outlet)
    witholding.rows.forEach((row) => {
      html += createRow(row.label, currency(row.amount))
    })

    html += createRow('Total tagihan', currency(witholding.amountAfterTax))
  }

  // Payment Section (for struk only)
  if (printerFunction === 'struk') {
    if (receiptData.payments && receiptData.payments.length > 0) {
      html += createLine('=')
      html += '<br>'

      receiptData.payments.forEach((payment, index) => {
        let amountAfterTax = payment.amount_after_tax || 0
        const payback = payment.payback || 0

        if (payback > 0) {
          amountAfterTax = payment.amount_after_tax + payback
        }
        amountAfterTax = witholding.getPaymentAmount(
          amountAfterTax,
          index,
          receiptData.payments.length,
        )

        let paymentLabel = payment.payment_type?.name || payment.payment_type_name || ''
        if (payment.payment_fee > 0) {
          paymentLabel += ' + Biaya Tambahan'
        }

        html += createRow(paymentLabel, currency(amountAfterTax))
      })

      const totalPayment = receiptData.payments.reduce(
        (sum, p) => sum + (p.amount_after_tax || 0),
        0,
      )
      html += createRow('Total bayar', currency(totalPayment + witholding.addBack))
    }

    if (receiptData.payment_back_amount > 0) {
      html += createRow('Kembalian', currency(receiptData.payment_back_amount))
    }

    if (receiptData.message) {
      html += '<br>'
      html += '<div class="center">### Catatan ###</div>'
      html += '<br>'
      html += `<div class="left">${receiptData.message}</div>`
      html += '<br>'
    }
  }

  // Footer Section
  if (printerFunction === 'struk' || printerFunction === 'bill') {
    html += createLine('=')
    html += '<br>'

    if (isOptionEnabled(outletOptions.print_style_show_note) && outletOptions.print_style_note) {
      html += `<div class="center">${outletOptions.print_style_note}</div><br>`
    }

    if (
      isOptionEnabled(outletOptions.print_style_show_footer) &&
      outletOptions.print_style_footer
    ) {
      html += `<div class="center">${outletOptions.print_style_footer}</div><br>`
    }

    if (isOptionEnabled(outletOptions.print_style_show_social_media)) {
      html += '<br>'
      html += '<div class="center">Instagram: Uplines</div>'
      html += '<br>'
    }
  }

  // Jumlah cetak struk (setting lokal aktif) — optimistic: cetakan saat ini = count BE + 1
  if (printOptions.printReceiptCountActive && printerFunction === 'struk') {
    const printedNow = (Number(receiptData.print_receipt_count) || 0) + 1
    html += `<div class="center">Dicetak ${printedNow} kali</div>`
  }

  // Timestamp
  html += '<br>'
  html += `<div class="center">###${formatDate(new Date())}###</div>`
  html += '<br><br>'

  html += `
    </body>
    </html>
  `

  return html
}

/**
 * Print via QZ Tray HTML Printing
 * Uses QZ Tray pixel printing with HTML format
 */
const printViaQZTrayHTML = async (receiptData, printOptions = {}) => {
  try {
    console.log('🖨️ Starting QZ Tray HTML print...')

    // Ensure connected
    await connectQZTray()

    // Get printer name
    const { printerName: providedPrinterName } = printOptions
    let printerName = providedPrinterName

    if (!printerName) {
      // Try to get default printer or first available
      try {
        const printers = await qz.printers.find()
        printerName = printers.length > 0 ? printers[0] : null
      } catch (error) {
        console.warn('Could not get printer list, using default')
        printerName = null
      }
    }

    console.log('Using printer:', printerName || 'Default')

    // Generate HTML content
    const htmlContent = generateReceiptHTML(receiptData, printOptions)

    // =====================
    // RESOLVE PAPER
    // =====================
    // Pakai paperSize untuk printer biasa: 'A4' | 'A5' | 'LETTER' | 'LEGAL'
    // Tetap support paperWidth kamu yg lama
    const paperInput = printOptions.paperSize || printOptions.paperWidth || 'A4'
    const paper = resolvePaperInches(paperInput)

    // =====================
    // QZ CONFIG (PENTING: units & size harus konsisten)
    // =====================
    // Untuk QZ HTML(pixel), paling aman pakai INCH agar nyambung dengan pageWidth/pageHeight
    const config = qz.configs.create(printerName, {
      units: 'in',
      size: { width: paper.width, height: paper.height },
      orientation: printOptions.orientation || 'portrait',

      // Ini yang biasanya menyelesaikan kasus “kecil di kiri”
      scaleContent: true,

      // Margin kecil agar tidak kepotong pinggir (driver printer biasa sering punya non-printable area)
      // Naikkan kalau masih kepotong: 2 -> 4 / 6
      margins: {
        top: 0.2,
        right: 0.2,
        bottom: 0.2,
        left: 0.2, // ⬅️ naikin kiri
      },
    })

    // Prepare data options
    // Jika paperWidth tidak di-set, biarkan QZ Tray auto-detect dari printer (A4, A5, dll)
    const dataOptions = {}

    // Hanya set pageWidth jika user specify paperWidth
    // Jika tidak di-set, QZ Tray akan menggunakan ukuran kertas default dari printer
    if (printOptions.paperWidth) {
      // Get paper width in inches
      // 80mm = 3.15 inches, 58mm = 2.28 inches
      const getPageWidthInches = (paperWidth) => {
        switch (paperWidth) {
          case '80mm':
          case 80:
            return 3.15 // 80mm in inches
          case '58mm':
          case 58:
            return 2.28 // 58mm in inches
          case 'A4':
            return 8.27 // A4 width in inches (210mm)
          case 'A5':
            return 5.83 // A5 width in inches (148mm)
          default:
            // Jika number, assume sudah dalam inches
            if (typeof paperWidth === 'number') {
              return paperWidth
            }
            return null // Auto-detect dari printer
        }
      }

      const pageWidthInches = getPageWidthInches(printOptions.paperWidth)
      if (pageWidthInches !== null) {
        dataOptions.pageWidth = pageWidthInches
      }
    }
    // Jika paperWidth tidak di-set, tidak set pageWidth di options
    // QZ Tray akan auto-detect ukuran kertas dari printer (A4, A5, dll)

    // Print HTML using QZ Tray pixel printing
    // Options pageWidth harus di dalam data array, bukan config
    const data = [
      {
        type: 'pixel',
        format: 'html',
        flavor: 'plain', // Use 'plain' for raw HTML string
        data: htmlContent,
        ...(Object.keys(dataOptions).length > 0 && { options: dataOptions }),
      },
    ]

    await qz.print(config, data)

    console.log('✅ Print via QZ Tray HTML successful!')
    return {
      success: true,
      message: 'Struk berhasil dicetak',
      method: 'browser-qz-tray',
      printer: printerName,
    }
  } catch (error) {
    console.error('❌ QZ Tray HTML print failed:', error)
    throw error
  }
}

/**
 * Print via browser window.print() (fallback)
 */
const printViaWindowPrint = async (receiptData, printOptions = {}) => {
  return new Promise((resolve, reject) => {
    try {
      console.log('🖨️ Starting window.print() fallback...')

      // Generate HTML content
      const htmlContent = generateReceiptHTML(receiptData, printOptions)

      // Create print window
      const printWindow = window.open('', '_blank', 'width=800,height=600')

      if (!printWindow) {
        reject(new Error('Popup diblokir. Izinkan popup untuk melanjutkan.'))
        return
      }

      // Write HTML content to print window
      printWindow.document.write(htmlContent)
      printWindow.document.close()

      // Wait for content to load, then print
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print()

          // Close window after print dialog
          setTimeout(() => {
            printWindow.close()
          }, 1000)
        }, 500)
      }

      console.log('✅ Browser print initiated')
      resolve({
        success: true,
        message: 'Print dialog dibuka. Silakan pilih printer.',
        method: 'browser-window',
      })
    } catch (error) {
      console.error('❌ Browser print failed:', error)
      reject(new Error(`Gagal mencetak via browser: ${error.message}`))
    }
  })
}

/**
 * Print via browser (QZ Tray HTML or window.print fallback)
 */
export const printViaBrowser = async (receiptData, printOptions = {}) => {
  try {
    // Try QZ Tray HTML printing first
    if (isQZTrayAvailable()) {
      try {
        return await printViaQZTrayHTML(receiptData, printOptions)
      } catch (error) {
        console.warn('QZ Tray HTML print failed, falling back to window.print():', error)
        // Fall through to window.print()
      }
    }

    // Fallback to window.print()
    return await printViaWindowPrint(receiptData, printOptions)
  } catch (error) {
    console.error('❌ Browser print failed:', error)
    throw new Error(`Gagal mencetak via browser: ${error.message}`)
  }
}

/**
 * Check if browser print is available (always true)
 */
export const isBrowserPrintAvailable = () => {
  return typeof window !== 'undefined' && typeof window.print === 'function'
}

export default {
  print: printViaBrowser,
  isAvailable: isBrowserPrintAvailable,
}
